<script setup>
import {computed, defineProps, reactive, watch, onMounted} from 'vue'
import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import {XIcon} from '@heroicons/vue/outline'
import {usePage, Link} from "@inertiajs/inertia-vue3";
import Cart from "../../Cart";
import emitter from 'tiny-emitter/instance'
import { Inertia } from "@inertiajs/inertia";

const themeColor = computed(() => usePage().props.value.theme_color);

let products = reactive(new Cart().storage.getCart());
const cart = new Cart();

function updateCart() {
  const cartItems = cart.getItems();
  // Empty cart panel before adding items.
  while(products.length > 0) {
    products.pop();
  }
  cartItems.map(i => {
    products.push(i)
  })

  emitter.emit('updateCart')
}

function removeItemFromCart(productIdx){
  cart.removeItem(productIdx);
  updateCart()
}

const props = defineProps({
  modelValue: Boolean
})

const emit = defineEmits(['update:modelValue'])

const closePanel = (event) => {
  emit('update:modelValue', false)
}

const goToCheckout = (event) => {
  closePanel();
  Inertia.visit(route('checkout.index', {
    method: 'get',
    replace: true,
  }))
}

watch(() => props.modelValue, (val) => {
  if (val) {
    updateCart()
  }
});

</script>
<template>
  <TransitionRoot as="template" :show="modelValue">
    <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="closePanel">
      <div class="absolute inset-0 overflow-hidden z-40">
        <TransitionChild as="template" enter="ease-in-out duration-500" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in-out duration-500" leave-from="opacity-100" leave-to="opacity-0">
          <DialogOverlay class="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
          <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700" enter-from="translate-x-full" enter-to="translate-x-0" leave="transform transition ease-in-out duration-500 sm:duration-700" leave-from="translate-x-0" leave-to="translate-x-full">
            <div class="pointer-events-auto w-screen max-w-md">
              <div class="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                <div class="flex-1 overflow-y-auto py-6 px-4 sm:px-6">
                  <div class="flex items-start justify-between">
                    <DialogTitle class="text-lg font-medium text-gray-900"> Uw bestelling </DialogTitle>
                    <div class="ml-3 flex h-7 items-center">
                      <button type="button" class="-m-2 p-2 text-gray-400 hover:text-gray-500" @click="closePanel">
                        <span class="sr-only">Sluiten</span>
                        <XIcon class="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div class="mt-8">
                    <div class="flow-root">
                      <ul role="list" class="-my-6 divide-y divide-gray-200">

                        <li v-for="(product, productIdx) in products" :key="productIdx" class="flex py-6">
                          <div class="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border border-gray-200">
                            <img :src="product.imageSrc" alt="" class="h-full w-full object-cover object-center" />
                          </div>

                          <div class="ml-4 flex flex-1 flex-col">
                            <div>
                              <div class="flex justify-between text-base font-medium text-gray-900">
                                <h3>
                                  <a :href="product.href"> {{ product.name }} </a>
                                </h3>
                                <p class="ml-4">€{{ product.price }} p/d</p>
                              </div>
                              <p class="mt-1 text-sm text-gray-500">Ophaaldatum: {{ new Date(product.pickupDate).toLocaleDateString('nl-NL') }}</p>
                              <p class="mt-1 text-sm text-gray-500">Retourdatum: {{ new Date(product.dropOffDate).toLocaleDateString('nl-NL') }}</p>
                              <p class="mt-1 text-sm text-gray-500">Ophaaltijd: {{ product.pickupTime }}</p>
                            </div>
                            <div class="flex flex-1 items-end justify-between text-sm mt-1">
                              <div class="flex">
                                <button :style="{'color': themeColor}" @click="removeItemFromCart(productIdx)" type="button" class="font-medium hover:text-indigo-500">Verwijder</button>
                              </div>
                            </div>
                          </div>
                        </li>

                        <li v-if="products.length === 0" class="py-12">
                          <p class="text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="inline-block text-gray-300 dark:text-gray-500 h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>

                          </p>

                          <p class="mt-3 text-center text-gray-500">
                            Er zijn geen reserveringen gevonden.
                          </p>
                        </li>

                      </ul>


                    </div>
                  </div>
                </div>

                <div class="border-t border-gray-200 py-6 px-4 sm:px-6">
                  <div class="mt-6">
                    <button @click="goToCheckout" :style="{'background-color': themeColor}" class="flex items-center justify-center rounded-md border border-transparent px-6 py-3 text-base font-medium text-white shadow-sm hover:opacity-80">Reserveren</button>
                  </div>
                  <div class="mt-6 flex justify-center text-center text-sm text-gray-500">
                    <p>
                      of <button type="button" class="font-medium text-primary" @click="closePanel">Ga door met zoeken<span aria-hidden="true"> &rarr;</span></button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
